import classNames from 'classnames';
import { Url } from 'next/dist/shared/lib/router/router';
import React, { FC } from 'react';

import Button, { ButtonProps } from '.';
import { TextLink } from '../Text';

import style from './menu-button.module.css';

export interface MenuButtonItem {
  href: Url;
  label: string;
  replace?: boolean;
  target: null | string | undefined;
  underlined?: boolean;
}

interface Props extends Omit<ButtonProps, 'caretProps' | 'id' | 'onClick'> {
  id: string;
  menuItems: MenuButtonItem[];
}

// Component Definition
const MenuButton: FC<Props> = ({
  menuItems,
  ...props
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const handleToggleMenu = () => {
    setIsMenuOpen(prev => !prev);
  };

  React.useEffect(() => {
    if (isMenuOpen) {
      const handleClickOutsideMenu = (evt: MouseEvent) => {
        const element = document.getElementById(props.id);
        if (element && !evt.composedPath().includes(element)) {
          setIsMenuOpen(false);
        }
      };

      document.addEventListener('click', handleClickOutsideMenu);

      return () => {
        document.removeEventListener('click', handleClickOutsideMenu);
      };
    }
  }, [isMenuOpen, props.id]);

  return (
    <div className={style.menuButtonContainer}>
      <Button
        {...props}
        caretProps={{
          className: classNames(style.menuButtonCaret, isMenuOpen ? style.menuButtonCaretOpen : ''),
        }}
        onClick={handleToggleMenu}
      >
        {props.children}
      </Button>

      <ul className={classNames(style.menuButtonMenu, isMenuOpen ? style.menuButtonMenuOpen : '')}>
        {menuItems.map(menuItem => (
          <li key={menuItem.label}>
            <TextLink
              linkFragment={{
                target: menuItem.target ?? '_blank',
                title: menuItem.label,
                url: menuItem.href as string,
              }}
              onClick={handleToggleMenu}
              rel="noopener noreferrer"
              replace={menuItem.replace}
              underlined={menuItem.underlined}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MenuButton;
